import { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import Auth from "../Auth";
import { Text } from "../sudokit/components/Text";
import VStack from "../sudokit/Layout/VStack";

function ImportNovelTracking() {
  const [tracking, setTracking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  const fetchTracking = async (page: number) => {
    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * limit;
      const response = await Auth.fetchWithIdToken("/api/admin/importNovelTracking/page", {
        limit,
        offset,
      });

      if (response.success) {
        setTracking(response.tracking);
        // Set a high total for now since we don't have count from API
        setTotal(999999);
      } else {
        setError(response.error || "Failed to fetch tracking data");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTracking(currentPage);
  }, [currentPage]);

  const columns = useMemo(() => [
    {
      name: "ID",
      width: "220px",
      selector: (row: any) => row.id,
      cell: (row: any) => (
        <div className="font-mono text-sm text-app-primary-cta px-2 py-1 rounded">
          {row.id}
        </div>
      ),
      sortable: false,
    },
    {
      name: "Invocation ID",
      width: "220px",
      selector: (row: any) => row.invocationId,
      cell: (row: any) => (
        <div className="font-mono text-sm text-gray-600 truncate" title={row.invocationId}>
          {row.invocationId}
        </div>
      ),
      sortable: false,
    },
    {
      name: "Project ID",
      width: "220px",
      selector: (row: any) => row.projectId,
      cell: (row: any) => (
        <div className="font-mono text-sm text-gray-600 truncate" title={row.projectId}>
          {row.projectId}
        </div>
      ),
      sortable: false,
    },
    {
      name: "User ID",
      width: "220px",
      selector: (row: any) => row.userId,
      cell: (row: any) => (
        <div className="font-mono text-sm text-gray-600 truncate" title={row.userId}>
          {row.userId}
        </div>
      ),
      sortable: false,
    },
    {
      name: "Status",
      width: "200px",
      selector: (row: any) => {
        const history = row.tracking?.statusHistory || [];
        return history.length > 0 ? history[history.length - 1].status : "Unknown";
      },
      cell: (row: any) => {
        const history = row.tracking?.statusHistory || [];
        const currentStatus = history.length > 0 ? history[history.length - 1].status : "Unknown";

        return (
          <div className="text-sm">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              currentStatus === "completed" ? "bg-green-100 text-green-800" :
              currentStatus === "error" ? "bg-red-100 text-red-800" :
              currentStatus === "processing" ? "bg-blue-100 text-blue-800" :
              "bg-gray-100 text-gray-800"
            }`}>
              {currentStatus}
            </span>
          </div>
        );
      },
      sortable: false,
    },
    {
      name: "Created At",
      width: "160px",
      selector: (row: any) => new Date(row.createdAt).toLocaleDateString(),
      cell: (row: any) => (
        <div className="text-sm text-gray-600">
          {new Date(row.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      ),
      sortable: false,
    },
    {
      name: "Updated At",
      width: "160px",
      selector: (row: any) => new Date(row.updatedAt).toLocaleDateString(),
      cell: (row: any) => (
        <div className="text-sm text-gray-600">
          {new Date(row.updatedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      ),
      sortable: false,
    },
    {
      name: "Completed",
      width: "120px",
      selector: (row: any) => row.completedAt ? "Yes" : "No",
      cell: (row: any) => (
        <div className="text-sm">
          {row.completedAt ? (
            <span className="text-green-600 font-medium">✓ Yes</span>
          ) : (
            <span className="text-gray-500">No</span>
          )}
        </div>
      ),
      sortable: false,
    },
  ], []);

    const ExpandableComponent = ({ data }: any) => {
    const history = data.tracking?.statusHistory || [];

    return (
      <div className="border-l-4 border-blue-500 bg-gray-50 p-6 mx-4 my-2 rounded-r-lg">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Import Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium text-gray-600">Project ID:</div>
                <div className="font-mono text-gray-800">{data.projectId}</div>
              </div>
              <div>
                <div className="font-medium text-gray-600">User ID:</div>
                <div className="font-mono text-gray-800">{data.userId}</div>
              </div>
              <div>
                <div className="font-medium text-gray-600">Invocation ID:</div>
                <div className="font-mono text-gray-800">{data.invocationId}</div>
              </div>
              <div>
                <div className="font-medium text-gray-600">Completed:</div>
                <div className={data.completedAt ? "text-green-600" : "text-gray-500"}>
                  {data.completedAt ? new Date(data.completedAt).toLocaleString() : "Not completed"}
                </div>
              </div>
            </div>
          </div>

          {history.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Status History</h3>
              <div className="space-y-2">
                {history.map((entry: any, index: number) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-white rounded border">
                    <div className="text-xs text-gray-500 font-mono w-32">
                      {new Date(entry.timestamp).toLocaleString()}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      entry.status === "completed" ? "bg-green-100 text-green-800" :
                      entry.status === "error" ? "bg-red-100 text-red-800" :
                      entry.status === "processing" ? "bg-blue-100 text-blue-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {entry.status}
                    </div>
                  </div>
                )).reverse()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <VStack spacing={4}>
        <Text size="2xl" weight="medium">Import Novel Tracking</Text>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <Text weight="medium">Error: {error}</Text>
          </div>
        )}

        <DataTable
          columns={columns}
          data={tracking}
          responsive={true}
          progressPending={loading}
          expandableRows
          expandableRowsComponent={<ExpandableComponent />}
          pagination
          paginationServer
          paginationPerPage={limit}
          paginationRowsPerPageOptions={[25]}
          paginationTotalRows={total}
          onChangePage={(page) => {
            setCurrentPage(page);
          }}
          noDataComponent={
            !loading && tracking.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Text>No import tracking records found</Text>
              </div>
            ) : undefined
          }
        />
      </VStack>
    </div>
  );
}

export default ImportNovelTracking;
